import { Task } from './task';

export class File {
  static readonly fileStates = {
    draft: 'Draft',
    closed: 'Processed',
    to_correct: 'To correct',
    pending: 'Pending',
    rejected: 'Rejected',
    applicantPayment: 'Applicant Payment',
    action_required: 'Action required'
  };

  constructor(
    public file_id: string = '',
    public process_instance_id: string = '',
    public service_id: string = '',
    public labelCssClass: string = '',
    public name: string = '',
    public description: string | null = null,
    public fileState: string = '',
    public rowCssClass: string | null = null,
    public fileState_code: string | null = null,
    public filterStatus: string | null = null,
    public processStatus: string | null = null,
    public state: string | null = null,
    public created_at: Date | null = null,
    public submitted_at: Date | null = null,
    public modified_at: Date | null = null,
    public service_name: string | null = null,
    public metadata: {} | null = null,
    public data: { 'listing-value': string | null } | null = null,
    public business_entity_name: string | null = null,
    public tasks: Task[] | null = [],
    public certificates: any[] = [],
    public isDeletable: boolean = true,
    public display_status: string | null = null
  ) {}

  getLatestTask() {
    let latestTask = null;
    if (this.tasks?.length) {
      latestTask = this.tasks.slice(-1)[0];
    }

    return latestTask;
  }

  getFileStateTranslated(value: string): string {
    value = value.toLowerCase();
    switch (value) {
      case 'draft':
        return $localize`:@@file_state_draft:Draft`;
      case 'closed':
        return $localize`:@@file_state_closed:Closed`;
      case 'to correct':
        return $localize`:@@file_state_to_correct:To correct`;
      case 'processed':
        return $localize`:@@file_state_closed:Processed`;
      case 'pending':
        return $localize`:@@file_state_pending:Pending`;
      case 'rejected':
        return $localize`:@@file_state_rejected:Rejected`;
      case 'applicantPayment':
      case 'applicant payment':
        return $localize`:@@Applicant payment:Applicant payment`;
      case 'action required':
      case 'action_required':
        return $localize`:@@Action required:Action required`;
    }

    return value;
  }

  setFileState() {
    if (this.filterStatus) {
      // for part B
      this.processStatus = this.filterStatus;
    } else {
      this.processStatus =
        this.processStatus ?? this.state ?? this.fileState_code;
    }
    switch (this.processStatus) {
      case 'IN_PROCESS':
      case 'filepending':
        this.fileState = File.fileStates.pending;
        this.labelCssClass = 'label-warning';
        this.rowCssClass = 'info';
        this.fileState_code = 'pending';
        break;
      case 'COMPLETED':
      case 'filevalidated':
        this.fileState = File.fileStates.closed;
        this.labelCssClass = 'label-success';
        this.rowCssClass = 'success';
        this.fileState_code = 'closed';
        break;
      case 'filedecline':
        this.fileState = File.fileStates.to_correct;
        this.labelCssClass = 'label-info';
        this.rowCssClass = 'warning';
        this.fileState_code = 'to_correct';
        break;
      case 'filereject':
        this.fileState = File.fileStates.rejected;
        this.labelCssClass = 'label-danger';
        this.rowCssClass = 'danger';
        this.fileState_code = 'rejected';
        break;
      case 'applicantPayment':
        this.fileState = File.fileStates.applicantPayment;
        this.labelCssClass = 'label-warning';
        this.rowCssClass = 'warning';
        this.fileState_code = 'applicantPayment';
        break;
      default:
        this.fileState = File.fileStates.draft;
        this.labelCssClass = 'label-default';
        this.rowCssClass = 'default';
        this.fileState_code = 'draft';
    }
  }

  private static readonly statusConfig: Record<
    string,
    { fileState: string; css: string; row: string; code: string }
  > = {
    filepending: {
      fileState: File.fileStates.pending,
      css: 'label-warning',
      row: 'info',
      code: 'pending'
    },
    filevalidated: {
      fileState: File.fileStates.closed,
      css: 'label-success',
      row: 'success',
      code: 'closed'
    },
    filedecline: {
      fileState: File.fileStates.to_correct,
      css: 'label-info',
      row: 'warning',
      code: 'to_correct'
    },
    filereject: {
      fileState: File.fileStates.rejected,
      css: 'label-danger',
      row: 'danger',
      code: 'rejected'
    },
    applicantPayment: {
      fileState: File.fileStates.applicantPayment,
      css: 'label-warning',
      row: 'warning',
      code: 'applicantPayment'
    },
    action_required: {
      fileState: File.fileStates.action_required,
      css: 'label-warning',
      row: 'warning',
      code: 'action_required'
    },
    NEW: {
      fileState: File.fileStates.draft,
      css: 'label-default',
      row: 'default',
      code: 'draft'
    }
  };

  setListingState() {
    // Prefer backend-computed display_status (single source of truth)
    if (this.display_status) {
      this.applyDisplayStatus(this.display_status);
      return;
    }
    // Fallback: compute locally (backward compatibility)
    this.setFileState();
    let latestTask = this.getLatestTask();
    // Below is applicant partb role case
    if (
      latestTask &&
      latestTask.status === 'filepending' &&
      latestTask.name !== 'applicant' &&
      latestTask.assignee === 'applicant'
    ) {
      this.fileState = File.fileStates.action_required;
      this.labelCssClass = 'label-warning';
      this.rowCssClass = 'warning';
      this.fileState_code = 'action_required';
    }
  }

  private applyDisplayStatus(status: string) {
    const config =
      // eslint-disable-next-line security/detect-object-injection -- status from backend, mapped to known keys
      File.statusConfig[status] || File.statusConfig['NEW'];
    this.fileState = config.fileState;
    this.labelCssClass = config.css;
    this.rowCssClass = config.row;
    this.fileState_code = config.code;
  }
}

export enum AvailableFileListColumns {
  SERVICE_TITLE,
  ENTERPRISE_NAME,
  CREATED_AT,
  SUBMITTED_AT,
  STATUS,
  CERTIFICATE,
  ACTIONS
}
